import { useMemo } from "react";
import { useQuery } from "urql";
import { sortBy, uniq } from "lodash";

import { FoNavCdsIndex } from "./constants";
import { GET_FONAV_CDS_INSTRUMENTS, GET_FONAV_TIMESERIES } from "./queries";
import { FoNavCdsRiskInput, getFoNavCdsSeriesCandidates, getTimeseriesValue, resolveFoNavCdsSeries } from "./utils";

interface FoNavCdsSeriesResult {
    fetching: boolean;
    error: unknown;
    rows: FoNavCdsRiskInput[];
}

function toPriceByInstrumentId(timeseries: { values?: string[] | null; instrument?: { _id: string } | null }[] | null | undefined) {
    const priceByInstrumentId: Record<string, number | null> = {};
    (timeseries ?? []).forEach((series) => {
        if (series.instrument?._id) {
            priceByInstrumentId[series.instrument._id] = getTimeseriesValue(series.values);
        }
    });
    return priceByInstrumentId;
}

/**
 * Fetches the series around the expected on the run series of each CDS index and resolves each index to the
 * newest series priced on both dates. All candidates go into the same requests, so no extra round trips.
 */
export function useFoNavCdsSeries(indices: FoNavCdsIndex[], startDate: string, endDate: string): FoNavCdsSeriesResult {
    const dates = useMemo(() => [startDate, endDate], [startDate, endDate]);
    const candidateNames = useMemo(() => uniq(indices.flatMap((index) => getFoNavCdsSeriesCandidates(index, dates))), [indices, dates]);

    const [{ fetching: fetchingInstruments, error: errorInstruments, data: dataInstruments }] = useQuery({
        query: GET_FONAV_CDS_INSTRUMENTS,
        variables: { nameIn: candidateNames },
        pause: candidateNames.length === 0,
        requestPolicy: "cache-and-network"
    });

    const instrumentIds = useMemo(() => {
        if (!dataInstruments?.instruments) {
            return [];
        }
        return dataInstruments.instruments.map((instrument) => instrument._id);
    }, [dataInstruments]);

    const [{ fetching: fetchingStart, error: errorStart, data: dataStart }] = useQuery({
        query: GET_FONAV_TIMESERIES,
        variables: {
            filter: {
                instrumentIdIn: instrumentIds,
                typeIn: "Price(Close)",
                startDate,
                endDate: startDate
            }
        },
        pause: instrumentIds.length === 0 || !startDate,
        requestPolicy: "network-only"
    });

    const [{ fetching: fetchingEnd, error: errorEnd, data: dataEnd }] = useQuery({
        query: GET_FONAV_TIMESERIES,
        variables: {
            filter: {
                instrumentIdIn: instrumentIds,
                typeIn: "Price(Close)",
                startDate: endDate,
                endDate
            }
        },
        pause: instrumentIds.length === 0 || !endDate,
        requestPolicy: "network-only"
    });

    const rows = useMemo((): FoNavCdsRiskInput[] => {
        if (!dataInstruments?.instruments || !dataStart?.timeseries || !dataEnd?.timeseries) {
            return [];
        }
        const resolved = resolveFoNavCdsSeries(
            indices,
            dates,
            dataInstruments.instruments,
            toPriceByInstrumentId(dataStart.timeseries),
            toPriceByInstrumentId(dataEnd.timeseries)
        );
        return sortBy(
            resolved.filter((row) => row.instrumentId),
            "name"
        );
    }, [indices, dates, dataInstruments, dataStart, dataEnd]);

    return {
        fetching: fetchingInstruments || fetchingStart || fetchingEnd,
        error: errorInstruments || errorStart || errorEnd,
        rows
    };
}
